import { z } from "zod";
import type { FinancialYear } from "../types.js";

// ---------------------------------------------------------------------------
// cgt — Capital Gains Tax on a sale (shares / property / crypto), individual.
// FY-versioned, pure `inputs -> rules -> outputs`. No I/O, no Date.now(); the
// assessment FY AND the contract dates are always parameters.
//
// Like `income-tax`, this is NOT a per-product ruleset (au06/au08/au13) — it
// carries an `engine: "cgt"` tag plus the usual provenance fields. The marginal
// tax on the net gain is NOT re-derived here: `calculate.ts` imports the shared
// `income-tax` engine and stacks the net gain on top of the user's other
// taxable income (ATO: a net capital gain is added to assessable income and
// taxed at the marginal rate — there is no separate/flat CGT rate).
//
// Regulatory posture (panel cgt-on-sale lens 2, cgt-wash-sale-checker lens 2):
//   - INFORMATION, not advice. No "sell now to save tax", no "minimise/reduce
//     your CGT", no product recommendation. Outputs are ESTIMATES; the ATO
//     finalises on lodgement.
//   - HARD RULE (C2 AFSL kill-line): a personalised CGT result must NEVER route
//     to a financial-product handoff (broker / trading platform / crypto
//     exchange) — shares/ETFs are s764A financial products, crypto exchanges
//     increasingly captured. `assertNoFinancialProductHandoff` enforces this in
//     code over every result, with a test.
//   - The wash-sale flag is a NEUTRAL informational warning ("this pattern may
//     attract ATO Part IVA attention — see a registered tax agent"), never a
//     safe-harbour clearance and never advice to sell/hold/harvest/re-buy.
// ---------------------------------------------------------------------------

/**
 * Asset class being disposed of. Drives only copy/flagging nuance (e.g. crypto
 * swaps are CGT events too, and the wash-sale warning explicitly extends to
 * crypto). The CGT mechanics are identical across classes for a resident
 * individual; the engine never decides residency or main-residence exemption.
 */
export const AssetClassSchema = z.enum(["shares", "property", "crypto", "other"]);
export type AssetClass = z.infer<typeof AssetClassSchema>;

/**
 * The five cost-base elements (ITAA97 s110.25 / ATO "Cost base of assets").
 * The capital GAIN uses the full cost base (all five). Note: the 3rd element
 * (costs of owning) is EXCLUDED from the *reduced* cost base used to work out a
 * capital LOSS — we surface `reducedCostBase` alongside `costBase` so a loss is
 * computed on the correct (smaller) base, never overstated.
 */
export const CostBaseInputSchema = z.object({
  /** Element 1 — money paid / market value of property given to acquire. */
  acquisitionCost: z.number().min(0),
  /** Element 2 — incidental costs to acquire AND to dispose (stamp duty,
   * brokerage, legal, conveyancing, valuation, agent commission, advertising). */
  incidentalCosts: z.number().min(0).default(0),
  /** Element 3 — costs of owning (rates, land tax, insurance, non-deductible
   * interest, maintenance). EXCLUDED from the reduced cost base (loss) per ATO. */
  ownershipCosts: z.number().min(0).default(0),
  /** Element 4 — capital improvement costs (enhancing the asset's value). */
  capitalImprovementCosts: z.number().min(0).default(0),
  /** Element 5 — capital expenditure to establish/preserve/defend title. */
  titleCosts: z.number().min(0).default(0),
});
export type CostBaseInput = z.input<typeof CostBaseInputSchema>;
/** Cost base after Zod defaults are filled (all five elements present). */
export type CostBaseParsed = z.infer<typeof CostBaseInputSchema>;

/**
 * A single disposal of a single parcel (v1 scope: single-parcel; FIFO/specific
 * parcel selection is a deliberate v2 — no live transaction-history dependency).
 *
 * Both dates are CONTRACT dates (the date you enter the contract to acquire /
 * dispose), NOT settlement dates — the CGT event happens on the contract date,
 * and the >12-month discount test runs contract-to-contract.
 */
export const CgtOnSaleInputSchema = z.object({
  assetClass: AssetClassSchema.default("shares"),
  /** Capital PROCEEDS from the disposal (sale price / market value received). */
  proceeds: z.number().min(0),
  /** Cost-base elements. */
  costBase: CostBaseInputSchema,
  /** Contract date the asset was ACQUIRED (ISO yyyy-mm-dd). CONTRACT, not
   * settlement. The >12-month test excludes this day. */
  acquisitionContractDate: z.string(),
  /** Contract date of the DISPOSAL / CGT event (ISO yyyy-mm-dd). CONTRACT, not
   * settlement. The >12-month test excludes this day too. */
  disposalContractDate: z.string(),
  /**
   * Other taxable income for the FY (salary etc.) the net capital gain stacks
   * ON TOP of, so the marginal-rate tax on the gain is the EXTRA tax it causes —
   * never the gain taxed in isolation. Defaults to 0.
   */
  otherTaxableIncome: z.number().min(0).default(0),
  /** Whether the person has an outstanding HELP/STSL loan — a net capital gain
   * lifts repayment income, so it can pull extra compulsory repayment. The CGT
   * tax figure reports income-tax + Medicare only; this gates a separate HELP
   * delta line. Defaults to false. */
  hasStudyLoan: z.boolean().default(false),
  /** The financial year of the DISPOSAL, e.g. "2025-26". Passed in (never from
   * the clock) so historical re-runs resolve against the right ruleset/scale. */
  assessmentYear: z.string(),
});
export type CgtOnSaleInput = z.input<typeof CgtOnSaleInputSchema>;

export interface CgtOnSaleOutput {
  /** Full cost base (all five elements) used for a GAIN. */
  costBase: number;
  /** Reduced cost base (excludes element 3) used for a LOSS. */
  reducedCostBase: number;
  /** Capital proceeds the calc ran against. */
  proceeds: number;
  /** Gross capital gain (proceeds − cost base), or 0 if a loss. */
  grossCapitalGain: number;
  /** Capital loss (reducedCostBase − proceeds), or 0 if a gain. A loss is NOT
   * discounted and NEVER offsets ordinary income — it offsets capital gains only
   * and carries forward (see `lossApplicationOrder`). */
  capitalLoss: number;
  /** Days held, contract-to-contract, EXCLUDING both endpoints (ATO method). */
  daysHeld: number;
  /** Whether the >12-month CGT-discount holding test is met (daysHeld > 365). */
  discountEligible: boolean;
  /** The 50% discount applied as a fraction (0.5 if eligible, else 0). */
  discountRate: number;
  /** Net capital gain after the discount (the amount added to assessable
   * income). Equals grossCapitalGain × (1 − discountRate). */
  netCapitalGain: number;
  /** Marginal income-tax + Medicare attributable to the net gain: tax on
   * (otherIncome + netGain) minus tax on otherIncome alone. The gain is taxed on
   * top of other income at the marginal scale — never in isolation. */
  taxOnGain: number;
  /** Marginal income-tax rate that bites the next dollar of net gain. */
  marginalRate: number;
  /** Extra compulsory HELP/STSL repayment the gain pulls (0 if no loan). */
  helpRepaymentDelta: number;
  /** Always true — every CGT figure is an estimate; the ATO finalises. */
  isEstimate: true;
}

// ---------------------------------------------------------------------------
// Loss application order
// ---------------------------------------------------------------------------

/**
 * Inputs for the loss-ordering surface. The ATO order (CITED) is the binding
 * mechanic: capital losses are subtracted from the GROSS gains BEFORE the 50%
 * discount, current-year losses first, then carried-forward losses, and — for
 * the best (lowest-CGT) result — applied to NON-discountable gains before
 * discountable gains. We model the two-bucket case (discountable vs
 * non-discountable gross gains) because that is where mis-ordering changes the
 * answer; the discount is applied to what remains of the discountable bucket.
 */
export const LossApplicationInputSchema = z.object({
  /** Gross gains eligible for the 50% discount (held >12 months), pre-discount. */
  discountableGains: z.number().min(0).default(0),
  /** Gross gains NOT eligible for the discount (held ≤12 months). */
  nonDiscountableGains: z.number().min(0).default(0),
  /** Capital losses realised THIS year (applied first). */
  currentYearLosses: z.number().min(0).default(0),
  /** Net capital losses carried forward from prior years (applied second). */
  carriedForwardLosses: z.number().min(0).default(0),
  /** Discount rate for the discountable bucket (default 0.5). */
  discountRate: z.number().min(0).max(1).default(0.5),
});
export type LossApplicationInput = z.input<typeof LossApplicationInputSchema>;

export interface LossApplicationOutput {
  /** Total losses available (current-year + carried-forward). */
  totalLossesAvailable: number;
  /** Losses actually applied against gains this year. */
  lossesApplied: number;
  /** Non-discountable gain remaining after losses (taxed in full). */
  nonDiscountableAfterLosses: number;
  /** Discountable gain remaining after losses, BEFORE the discount. */
  discountableAfterLosses: number;
  /** Net capital gain = nonDiscountableAfterLosses + discountableAfterLosses ×
   * (1 − discountRate). This is the amount added to assessable income. */
  netCapitalGain: number;
  /** Net capital LOSS carried forward to future years (unused losses). Offsets
   * capital gains only — never ordinary income — and carries forward
   * indefinitely. */
  carryForwardToNextYear: number;
  /** Whether any loss was left unused (and so carried forward). */
  hasCarryForward: boolean;
  /** Always true — estimate, ATO finalises. */
  isEstimate: true;
}

// ---------------------------------------------------------------------------
// Wash-sale flag
// ---------------------------------------------------------------------------

/**
 * Inputs for the wash-sale risk flag. There is NO US-style fixed window (TA
 * 2008/7 is a dominant-purpose / Part IVA test); the flag is intention- and
 * economic-exposure based. `daysToReacquisition` is informational context only,
 * never a bright line that "clears" the user.
 */
export const WashSaleInputSchema = z.object({
  /** Did the disposal crystallise a capital LOSS? (precondition for the pattern) */
  soldAtLoss: z.boolean(),
  /** Did/does the person intend to reacquire the same or a substantially
   * identical asset? */
  reacquiredSameOrSubstantiallyIdentical: z.boolean(),
  /** Was the dominant purpose of the disposal to generate the tax loss (rather
   * than a genuine change in economic exposure)? */
  dominantPurposeTaxBenefit: z.boolean(),
  /** Optional: days between the sale and the (intended) reacquisition. Context
   * only — there is NO set period; a short gap raises attention but no gap is
   * a safe harbour. */
  daysToReacquisition: z.number().int().min(0).optional(),
});
export type WashSaleInput = z.infer<typeof WashSaleInputSchema>;

export interface WashSaleOutput {
  /** Neutral risk read — never a clearance. "elevated" when the disposal+
   * reacquisition+dominant-purpose pattern is present; "not-indicated" when a
   * precondition is absent. The ATO applies Part IVA on the facts; this is not a
   * ruling on the user's situation. */
  risk: "elevated" | "not-indicated";
  /** Whether the full wash-sale pattern (loss + reacquisition + dominant tax
   * purpose) is present. */
  patternPresent: boolean;
  /** Which precondition(s) are absent — surfaced so the read is explainable,
   * never a "you're safe" verdict. */
  missingPreconditions: readonly WashSalePrecondition[];
  /** Always true: there is NO fixed time window (no US 30-day rule); the test is
   * intention-based. Stated so no output can read as "wait N days and you're
   * fine". */
  noFixedTimeWindow: true;
  /** Always true — informational warning, not advice, not a ruling. */
  isInformationalWarning: true;
}

export type WashSalePrecondition =
  | "sold-at-loss"
  | "reacquired-same-or-substantially-identical"
  | "dominant-purpose-tax-benefit";

// ---------------------------------------------------------------------------
// Ruleset
// ---------------------------------------------------------------------------

/** A complete FY CGT ruleset, keyed by financial year. */
export interface CgtRuleSet {
  /** Engine tag — distinguishes shared engines from per-product rulesets. */
  engine: "cgt";
  /** Financial-year key, e.g. "2025-26". */
  fy: FinancialYear;
  /** Inclusive FY start (1 July). */
  effectiveFrom: string;
  /** Inclusive FY end (30 June). */
  effectiveTo: string;
  /** Source-of-truth URLs the figures were transcribed from. */
  sources: Record<string, string>;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;
  /** CGT discount fraction for a resident individual (0.5 = 50%). */
  discountRate: number;
  /** Minimum DAYS held for the discount — the >12-month test excludes both
   * endpoints, so the asset must be held a minimum of 367 calendar days, i.e.
   * `daysHeld > minDaysHeldForDiscount` where daysHeld already excludes both
   * endpoints (so the boundary is 365: daysHeld must exceed 365). */
  minDaysHeldForDiscount: number;
}
